/** Server refusals (`ack` and `room.error`) in words a player understands. */
import type { AckError, RoomErrorCode } from '@sa/protocol';
import { BALANCE, TICK_HZ } from '@sa/sim';

const mmss = (ticks: number) => `${Math.floor(ticks / TICK_HZ / 60)}:${String(Math.floor(ticks / TICK_HZ) % 60).padStart(2, '0')}`;

export const ACK_TEXT: Record<AckError, string> = {
  matchOver: 'MATCH IS OVER',
  unknownPlayer: 'NOT IN THIS MATCH',
  eliminated: 'YOU ARE ELIMINATED',
  badIntent: 'INVALID ORDER',
  badHex: 'INVALID SITE',
  badTarget: 'INVALID TARGET',
  notFound: 'UNIT NO LONGER EXISTS',
  notOwner: 'NOT YOUR UNIT',
  badKind: 'THAT UNIT CANNOT DO THIS',
  credits: 'NOT ENOUGH CREDITS',
  cap: 'LIMIT REACHED',
  notOwnHex: 'OWN LAND ONLY',
  occupied: 'HEX OCCUPIED',
  radiation: 'IRRADIATED GROUND',
  spacing: 'TOO CLOSE TO A CITY',
  notPort: 'NEEDS A COASTAL HEX',
  notBuilding: 'NOT UNDER CONSTRUCTION',
  notReady: 'STILL UNDER CONSTRUCTION',
  maxLevel: 'CITY AT MAX LEVEL',
  busy: 'ALREADY IN PROGRESS',
  full: 'MAGAZINE FULL',
  empty: 'NO MISSILE LOADED',
  notDocked: 'DOCK AT A PORT FIRST',
  releaseLock: `WEAPONS LOCKED · RELEASE AT ${mmss(BALANCE.matchTicks - BALANCE.phases.releaseTick)} LEFT`,
  inFlightCap: `MAX ${BALANCE.missiles.inFlightCap} MISSILES IN FLIGHT`,
  allyTarget: 'CANNOT TARGET AN ALLY',
  betrayalLock: `YOU BROKE THE DEAL · NO STRIKES FOR ${BALANCE.alliance.breakNoTargetTicks / TICK_HZ}S`,
  range: 'OUT OF RANGE',
  cooldown: 'RELOADING',
  noAircraft: 'NO AIRCRAFT READY',
  unreachable: 'CANNOT REACH THAT POINT',
  allianceMode: 'NO ALLIANCES IN TEAM MATCHES',
  allianceSize: 'ONE ALLY AT A TIME',
  allianceCooldown: 'ALLIANCE COOLDOWN',
  noRequest: 'PROPOSAL EXPIRED',
  alreadyAllied: 'ALREADY ALLIED',
  notAllied: 'NOT ALLIED',
  self: 'THAT IS YOU',
  ceasefire: 'CEASEFIRE IN FORCE',
  ceasefireCooldown: 'CEASEFIRE COOLDOWN',
  noCeasefire: 'NO CEASEFIRE',
  rateLimited: 'SLOW DOWN',
  notInMatch: 'NOT IN A MATCH',
};

export function ackText(err: string | undefined): string {
  return (err && ACK_TEXT[err as AckError]) || 'ORDER REFUSED';
}

/** Lobby / room refusals (`room.error`) in player words; never the server's raw text. */
export const ROOM_TEXT: Record<RoomErrorCode, string> = {
  badMessage: 'SOMETHING WENT WRONG · TRY AGAIN',
  unauthorized: 'SESSION EXPIRED · SIGN IN AGAIN',
  rateLimited: 'SLOW DOWN',
  notFound: 'ROOM NOT FOUND',
  full: 'ROOM FULL',
  notHost: 'ONLY THE HOST CAN DO THAT',
  notReady: 'NOT EVERYONE IS READY',
  badSlot: 'THAT SEAT CANNOT CHANGE',
  regionTaken: 'REGION TAKEN',
  alreadyInRoom: 'ALREADY IN A ROOM',
  notInRoom: 'NOT IN A ROOM ANY MORE',
  inMatch: 'MATCH ALREADY STARTED',
  resumeExpired: 'MATCH NO LONGER AVAILABLE',
  badTeam: 'NO SUCH TEAM',
  badRegion: 'THAT REGION IS NOT IN PLAY',
  notEnoughPlayers: 'ADD A BOT OR WAIT FOR A PLAYER',
  oneTeam: 'NEED PLAYERS ON 2 TEAMS',
};

/**
 * A room error for the request that caused it: a malformed join (the code cannot exist)
 * is simply "not found".
 */
export function roomErrorText(code: string, request: string | null): string {
  if (code === 'badMessage' && request === 'room.join') return ROOM_TEXT.notFound;
  return ROOM_TEXT[code as RoomErrorCode] ?? ROOM_TEXT.badMessage;
}
